// eslint-disable-prototype-builtins

import styles from './information-card.module.scss';

import { useTranslation } from 'react-i18next';
import { IconWrapper } from '@flexo/general';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { navigateToCommunity } from '@flexo/resolvers';
import { MODAL, RIGHT_DRAWER } from '@flexo/reducers';
import { navigateToSite } from '@flexo/resolvers';
import { navigateToMember } from '@flexo/resolvers';
import parse from 'html-react-parser';
import { Shimmer, CardSkeleton } from '@flexo/atoms';
import { useKpiData } from '@flexo/hooks';
import {
  formatWeek,
  formatDay,
  formatMonth,
  formatYear,
  checkItIsToday,
  getCardApiValue,
  formatValue,
} from './helpers';
import { checkKpiType } from '@flexo/general';
import {
  InformationCardContentTotalSitesCard,
  InformationCardContentLabelValueList,
  InformationCardContentLabel,
  InformationCardContentLabelValueUnit,
  InformationCardContentDescription,
  InformationCardContentKeyValue,
  InformationCardContentValueLabel,
  InformationCardContentLabelValue,
  InformationCardContentMemberLabelValue,
  InformationCardContentSiteTypeLabelValue,
  InformationCardContentCurlyTrees,
  InformationCardContentTrees,
  InformationCardContentRankingLabelValue,
  InformationCardContentDynamicLabel
} from './information-card-contents';

const ENV = (import.meta as any).env;
const browserLanguage = navigator.language;

export function InformationCard(props: any) {
  const {
    InformationCard__Icon,
    InformationCard__Value,
    InformationCard__Label,
    InformationCard__Label__Disabled,
    InformationCard__Detail,
  } = styles;

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const location = useLocation();
  const pathSegment = location.pathname.split('/')[1];
  const { id: urlID } = useParams();
  const metadata = useSelector((state: any) => state.metadata);
  const kpi = useSelector((state: any) => state.kpi);
  const calendar = useSelector((state: any) => state.calendar);
  const { selectedDate, selectedTimeSet } = calendar.hiveCalendar;

  const currencyFields = ['saving', 'benefit', 'economic', 'revenue'];

  let entity = 'Site';
  let kpiContext = 'Energy';

  if (pathSegment?.includes('economics')) {
    kpiContext = 'Economics';
  }

  if (pathSegment?.includes('community')) {
    entity = 'Community';
  }
  if (pathSegment?.includes('member')) {
    entity = 'Member';
  }

  if (props?.kpiContext === 'ancillary') {
    kpiContext = 'Ancillary';
  }

  if (props?.kpiContext === 'economics') {
    kpiContext = 'Economics';
  }

  const overviewKpiApiData =
    (kpi[`${kpiContext}${entity}`]?.['1day']?.[0]?.kpi as any) || null;
  const LOADING_STATE = kpi?.[`${kpiContext}KpiData${entity}AsyncStatus`];

  // Use the new hook for KPI data access
  const { getCardValue: getKpiCardValue, formatValue: formatKpiValue } =
    useKpiData(overviewKpiApiData, kpiContext);

  function getSiteRanking() {
    const site =
      (metadata?.entities as any)?.site?.find(
        (_site: any) =>
          _site.siteId ===
          kpi?.communityRanking?.economicsRanking?.[0]?.site?.[0]?.siteId
      ) || null;
    return site?.siteName || '';
  }

  const getDate = () => {
    let result = '';
    switch (selectedTimeSet) {
      case 'day':
        if (checkItIsToday(selectedDate)) result = t('widgets.calendar.today');
        else result = formatDay(selectedDate);
        break;

      case 'week':
        result = selectedDate[0]
          ? `${formatWeek(selectedDate)}`
          : 'Select Week';
        break;

      case 'month':
        result = formatMonth(selectedDate);
        break;

      case 'year':
        result = formatYear(selectedDate);
        break;

      default:
        if (
          formatDay(selectedDate) === formatDay([new Date(), new Date()] as any)
        )
          result = t('widgets.calendar.yesterday');
        else result = formatDay(selectedDate);
        break;
    }
    return result;
  };

  const onClickLabel = () => {
    switch (props?.env || '') {
      case 'web':
        if (props?.descriptionCallback === 'communityEnergy') {
          navigateToCommunity('community-energy', urlID as string);
        } else if (props?.descriptionCallback === 'siteEnergy') {
          navigateToSite('site-energy', urlID as string);
        } else if (props?.descriptionCallback === 'communityEconomics') {
          navigateToCommunity('community-economics', urlID as string);
        } else if (props?.descriptionCallback === 'MemberBenefits') {
          navigateToMember('member-economics', urlID as string);
        } else if (props?.descriptionCallback === 'memberEnergy') {
          navigateToMember('member-energy', urlID as string);
        } else if (
          props?.descriptionCallback === 'communityTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'COMMUNITY_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (
          props?.descriptionCallback === 'memberTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'MEMBER_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (
          props?.descriptionCallback === 'SiteTotalEstimatedBenefits'
        ) {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
              type: 'SITE_TOTAL_ESTIMATED_BENEFITS',
            })
          );
        } else if (props?.descriptionCallback === 'siteEconomics') {
          navigateToSite('site-economics', urlID as string);
        } else {
          dispatch(
            RIGHT_DRAWER.SetDrawer({
              ...props,
            })
          );
        }

        break;
      default:
        if (!props.details.title) return;

        dispatch(
          MODAL.OpenModal({
            type: 'CARD',
            visible: true,
            show: true,
            props: { ...props },
            content: 'CARD',
          })
        );

        break;
    }
  };

  const getCardValue = () => {
    let cardValue = getKpiCardValue(props['totalValue'], { currencyFields });

    if (props['totalValue'] === 'sites_count') {
      cardValue = {
        value: metadata?.sortedEntities?.communities[0]?.members.reduce(
          (prev, next) => prev + (next?.sites?.length || 0),
          0
        ),
        unit: '',
      };
    }

    return (
      <div
        key={`${props.id}-featured-data`}
        className={`${
          ENV?.VITE_APP_IS_WEB === 'true' &&
          styles.InformationCard__Web__FeaturedData__Wrapper
        }`}
      >
        <span
          key={`${props.id}-featured-data-value`}
          className={`featuredData ${
            ENV?.VITE_APP_IS_WEB === 'true' &&
            styles.InformationCard__Web__FeaturedData
          }`}
          style={{ fontWeight: 200 }}
        >
          {!cardValue?.value
            ? '-.-'
            : formatKpiValue(cardValue?.value, kpiContext)}
        </span>
        <span
          key={`${props.id}-featured-data-unit`}
          className="heading3L"
          style={{ fontWeight: 200 }}
        >
          {' ' + (cardValue?.unit || '')}
        </span>
      </div>
    );
  };

  const getCardDetail = () => {

    console.log('props', props);

    const cardValue = getKpiCardValue(props['descriptionValue'], { currencyFields });
    switch (props.descriptionType) {
      case 'totalSitesCard':
        return <InformationCardContentTotalSitesCard props={props} />;
      case 'web:label+value|label+value':
      case 'label+value|label+value':
        return (
          <InformationCardContentLabelValueList
            description={props.description}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case 'dynamic-label':
        return <InformationCardContentDynamicLabel additionalFields={props.additionalFields} description={props.description} id={props.id} overviewKpiApiData={overviewKpiApiData} />;
      case 'label':
        return (
          <InformationCardContentLabel
            description={props.description}
            id={props.id}
          />
        );
      case 'label+value+unit':
        return (
          <InformationCardContentLabelValueUnit
            description={props.description}
            value={props.value}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case 'description':
        return (
          <InformationCardContentDescription description={props.description} />
        );
      case 'keyValue':
        return (
          <InformationCardContentKeyValue
            description={props.description}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
          />
        );
      case 'value+label':
        return (
          <InformationCardContentValueLabel
            cardValue={cardValue}
            description={props.description}
            id={props.id}
          />
        );
      case 'label+value':
        return (
          <InformationCardContentLabelValue
            description={props.description}
            cardValue={cardValue}
            overviewKpiApiData={overviewKpiApiData}
            kpiContext={kpiContext}
            currencyFields={currencyFields}
            id={props.id}
          />
        );
      case '[member]label+value':
        return (
          <InformationCardContentMemberLabelValue
            KPIs={overviewKpiApiData}
            description={props.description}
            cardValue={cardValue}
            metadata={metadata}
            urlID={urlID}
            id={props.id}
          />
        );
      case 'siteType[label+value]':
        return (
          <InformationCardContentSiteTypeLabelValue
            description={props.description}
            cardValue={cardValue}
            overviewKpiApiData={overviewKpiApiData}
            id={props.id}
          />
        );
      case '{{trees}}':
        return (
          <InformationCardContentCurlyTrees
            description={props.description}
            cardValue={cardValue}
            t={t}
          />
        );
      case 'trees':
        return (
          <InformationCardContentTrees
            description={props.description}
            cardValue={cardValue}
            t={t}
          />
        );
      case 'ranking[label+value]':
        return (
          <InformationCardContentRankingLabelValue
            description={props.description}
            getSiteRanking={getSiteRanking}
            t={t}
            id={props.id}
          />
        );
      default:
        return null;
    }
  };

  const { id } = props;

  if (!props?.id) {
    return <></>;
  }

  return (
    <div
      className={` ${
        props.gradientBorder && styles.InformationCard__GradientBorder
      }`}
      style={{ height: '100%' }}
      key={`${props.id}-information-card`}
    >
      {LOADING_STATE === 'PENDING' && <Shimmer />}

      {(LOADING_STATE === 'PENDING' &&
        (overviewKpiApiData?.length === 0 || !overviewKpiApiData)) ||
      !props?.id ? (
        <CardSkeleton id={props.id} icon={props.icon} />
      ) : (
        <div
          className={`${styles.InformationCard} ${
            ENV?.VITE_APP_IS_WEB === 'true' && styles.InformationCard__Web
          }`}
        >
          {props.icon && (
            <span
              key={`${props.id}-icon`}
              className={`${InformationCard__Icon} ${
                ENV?.VITE_APP_IS_WEB === 'true' &&
                styles.InformationCard__Web__Icon
              }`}
            >
              {<IconWrapper iconName={props.icon} />}{' '}
              {props?.isOverview === true && (
                <span className="paragraph color-grey-500">{getDate()}</span>
              )}
            </span>
          )}
          {props?.totalValue && (
            <span className={`${InformationCard__Value} `}>
              {' '}
              {getCardValue()}
            </span>
          )}
          <div
            key={`${props.id}-label-value-label-value-array-ranking-label`}
            className={`${
              props?.detail === false
                ? InformationCard__Label__Disabled
                : InformationCard__Label
            } ${
              ENV?.VITE_APP_IS_WEB === 'true' &&
              styles.InformationCard__Web__Label
            } heading4`}
            onClick={() =>
              props?.detail === false ? undefined : onClickLabel()
            }
            style={{ marginTop: '16px' }}
          >
            {parse(t(`widgets.${id}.title`))}
            <IconWrapper
              className={'InformationCard__Chevron'}
              iconName="chevron-right"
              style={
                ENV?.VITE_APP_IS_WEB === 'true'
                  ? { marginTop: '1.5px' }
                  : { marginTop: -12 }
              }
            />
          </div>
          <span
            className={`${InformationCard__Detail} paragraph ${
              ENV?.VITE_APP_IS_WEB === 'true' &&
              styles.InformationCard__Web__Detail
            }`}
          >
            {getCardDetail()}
          </span>
        </div>
      )}
    </div>
  );
}

// ... existing code ...
/*

  const getCardDetail = () => {
    // console.log('props.descriptionType', props.descriptionType);
    const cardValue = getCardApiValue(props['descriptionValue']);

    switch (props.descriptionType) {
      case 'totalSitesCard':
        if (
          !(metadata as any)?.sortedEntities ||
          (metadata as any)?.entities?.length === 0
        ) {
          return <></>;
        }

        // eslint-disable-next-line
        let community = (metadata as any)?.sortedEntities?.communities.find(
          (community) =>
            community.id === metadata?.selectedEntityId ||
            community.communityId === metadata?.selectedEntityId
        );

        if (!community) {
          community = (metadata as any)?.sortedEntities?.communities[0];
        }

        // eslint-disable-next-line
        const siteTypeCounts = {
          consumer: 0,
          producer: 0,
          prosumer: 0,
          prosumage: 0,
        };

        community.members.forEach((member) => {
          member.sites.forEach((site) => {
            if (Array.isArray(site.siteType)) {
              site.siteType.forEach((type) => {
                if (
                  type.siteType &&
                  // eslint-disable-next-line
                  Object.prototype.hasOwnProperty.call((siteTypeCounts as any), type.siteType)
                ) {
                  siteTypeCounts[type.siteType]++;
                }
              });
            } else if (typeof site.siteType === 'string') {
              // eslint-disable-next-line
              if (siteTypeCounts.hasOwnProperty(site.siteType)) {
                siteTypeCounts[site.siteType]++;
              }
            }
          });
        });

        for (const key in siteTypeCounts) {
          if (siteTypeCounts[key] === 0) {
            siteTypeCounts[key] = null;
          }
        }

        // eslint-disable-next-line
        const keys = props.description.map((el) =>
          el.key.split('.').slice(-1).pop()
        );

        return (
          <>
            {keys
              .filter((_el: any) => siteTypeCounts[_el] !== null)
              .map((siteType: string, _i: number, array: any) => {
                const label = props.description.find((desc: any) =>
                  desc.key.includes(siteType)
                );

                return (
                  <span key={`${props.id}-label-value-label-value-${_i}`} className="paragraph">
                    {t(label.key)} {siteTypeCounts[siteType]}{' '}
                    {_i < array.length - 1 ? '|' : ''}
                  </span>
                );
              })}
          </>
        );
      case 'web:label+value|label+value':
        return (
          <span className="paragraph">
            {props.description.map((el, _i, array) => (
              <>
                {t(el.key)}{' '}
                { formatValue( getCardApiValue(el?.value).value ) + getCardApiValue(el?.value)?.unit ||
                  ''}{' '}
                {_i < array.length - 1 ? '| ' : ''}
              </>
            ))}
          </span>
        );
      case 'label+value|label+value':
        return (
          <div>
            {props.description.map((el, _i, array) => (
              <span key={`${props.id}-label-value-label-value`} className="paragraph">
                {t(el.key)}{' '}
                { formatValue(getCardApiValue(el?.value)?.value ||
                  '-.- ') + getCardApiValue(el?.value)?.unit ||
                  ''}{' '}
                {_i < array.length - 1 ? '|' : ''}
              </span>
            ))}
          </div>
        );
      case 'label':
        return (
          <span key={`${props.id}-label`} className="paragraph">
            {t(props.description)}
          </span>
        );
      case 'label+value+unit':
        return (
          <span key={`${props.id}-label-value-unit`} className="paragraph">
            {parse(
              t(props.description, {
                value: formatValue(getCardApiValue(props?.value)?.value || '-.-'),
                unit: getCardApiValue(props?.value)?.unit || '',
              })
            )}
          </span>
        );
      case 'description':
        return <div className="paragraph">{t(props.description)}</div>;
      case 'keyValue':
        // eslint-disable-next-line no-case-declarations

        // eslint-disable-next-line no-case-declarations
        const result = props.description?.map((el) => {
          return (
            <div>
              <div>
                <span className="paragraph">{t(el.key)} </span>
                <span className="heading5">
                  {' '}
                  { formatValue(getCardApiValue(el?.value)?.value || '-.- ') +
                    (getCardApiValue(el?.value)?.unit ||
                      getCardApiValue(el?.value)?.unit_type ||
                      '')}
                </span>
              </div>
            </div>
          );
        });

        return result;
      case 'value+label':
        return (
          <div>
            <span key={`${props.id}-value-label`} className="paragraph">
              {' '}
              <span className="heading5">
                {(cardValue?.value || '-.- ') + (cardValue?.unit || '')}
              </span>
            </span>
            <span key={`${props.id}-value-label-description`} className="paragraph">
              {t(props.description)}
            </span>
          </div>
        );
      case 'label+value':
        if (typeof props.description === 'string') {
          return (
            <div>
              <span key={`${props.id}-label-value-label-description`} className="paragraph">
                {t(props.description)}
              </span>
              <span key={`${props.id}-label-value-card-value`} className="paragraph">
                {' '}
                <span className="heading5">{cardValue?.value || '-.-'}</span>
              </span>
            </div>
          );
        } else if (Array.isArray(props.description)) {
          return (
            <div key={`${props.id}-label-value-label-value-array`}>
              {props.description.map((el, _i, array) => (
                <div key={`${props.id}-label-value-label-value-array-${_i}`}>
                  <span className={`color-hive-greyscale-600`}>
                    <span className="font-flexo-semibold heading5">
                      {t(el.key)}{' '}
                    </span>
                    { formatValue(getCardApiValue(el?.value)?.value || '-.- ') +
                      (getCardApiValue(el?.value)?.unit ||
                        getCardApiValue(el?.value)?.unit_type ||
                        '')}
                  </span>
                  {_i < array.length - 1 && <br />}
                </div>
              ))}
            </div>
          );
        }
        break;
      case '[member]label+value':
        if (typeof props.description === 'string') {
          return (
            <div>
              <span key={`${props.id}-label-value-label-description-label`} className="paragraph">
                {t(props.description)}
              </span>
              <span key={`${props.id}-label-value-card-value-card-value`} className="paragraph">
                {' '}
                <span className="heading5">{cardValue?.value || '-.-'}</span>
              </span>
            </div>
          );
        } else if (Array.isArray(props.description)) {

          let allowedIndex = 1;
          const memberSitesTypes = [...metadata?.sortedEntities?.communities?.map(c => c.members)?.flat() || []]?.find(m => m.memberId === urlID)?.sites?.map(s => s?.siteType?.[0].siteType) || [];
          if ( memberSitesTypes?.includes('producer') ) { allowedIndex++; }
          if ( memberSitesTypes?.includes('prosumer') ) { allowedIndex = 3; }
          if ( memberSitesTypes?.includes('prosumage') ) { allowedIndex = 3; }
          

          return (
            <div key={`${props.id}-label-value-label-value-array-member`}>
              {props.description.map((el, _i, array) => {
                if (_i < allowedIndex) {
                  return <div key={`${props.id}-label-value-label-value-array-member-${_i}`}>
                    <span className={`color-hive-greyscale-600`}>
                      <span className="font-flexo-semibold heading5">
                        {t(el.key)}{' '}
                      </span>
                      { formatValue(getCardApiValue(el?.value)?.value || '-.- ') +
                        (getCardApiValue(el?.value)?.unit ||
                          getCardApiValue(el?.value)?.unit_type ||
                          '')}
                    </span>
                    {_i < array.length - 1 && <br />}
                  </div>
                }
              })}
            </div>
          );
        }
        break;
      case 'siteType[label+value]':
        if (typeof props.description === 'string') {
          return (
            <div>
              <span key={`${props.id}-label-value-label-value-array-siteType-object-label`} className="paragraph">
                {t(
                  props?.[
                    (metadata?.selectedEntity as any)?.siteType?.[0]
                      ?.siteType || 'default'
                  ]?.description
                )}
              </span>
              <span key={`${props.id}-label-value-label-value-array-siteType-object-card-value`} className="paragraph">
                {' '}
                <span className="heading5">{cardValue?.value || '-.- '}</span>
              </span>
            </div>
          );
        } else if (Array.isArray(props.description)) {
          return (
            <div key={`${props.id}-label-value-label-value-array-siteType`}>
              {props.description?.map((el, _i, array) => (
                <div key={`${props.id}-label-value-label-value-array-siteType-${_i}`}>
                  <span className={`color-hive-greyscale-600`}>
                    <span className="font-flexo-semibold heading5">
                      {t(el.key)}{' '}
                    </span>
                    { formatValue(getCardApiValue(el?.value)?.value || '-.- ') +
                      (getCardApiValue(el?.value)?.unit ||
                        getCardApiValue(el?.value)?.unit_type ||
                        '')}
                  </span>
                  {_i < array.length - 1 && <br />}
                </div>
              ))}
            </div>
          );
        } else if (Object.keys(props?.description)?.length > 0) {
          return (
            <div key={`${props.id}-label-value-label-value-array-siteType-object`}>
              {props.description?.[
                (metadata?.selectedEntity as any)?.siteType?.[0]?.siteType ||
                  (metadata?.selectedEntity as any)?.type ||
                  'default'
              ]?.map((el, _i, array) => (
                <div key={`${props.id}-label-value-label-value-array-siteType-object-${_i}`}>
                  <span className={`color-hive-greyscale-600`}>
                    <span className="font-flexo-semibold heading5">
                      {t(el.key)}{' '}
                    </span>
                    { formatValue(getCardApiValue(el?.value)?.value || '-.- ') +
                      (getCardApiValue(el?.value)?.unit ||
                        getCardApiValue(el?.value)?.unit_type ||
                        '')}
                  </span>
                  {_i < array.length - 1 && <br />}
                </div>
              ))}
            </div>
          );
        }
        break;

      case '{{trees}}':
        return (
          <div>
            <span>
              {t(props.description.line_1)}
              <br />
              {t(props.description.line_2, { trees: formatValue( cardValue?.value || '-.-') })}
            </span>
          </div>
        );
        break;
      case 'trees':
        return (
          <div className="paragraph">
            <span className="paragraph">
              {parse(t(props.description.line_1))}
              <br />
              {parse(t(props.description.line_2))}
              <span
                style={{
                  fontSize: '14px !important',
                  fontWeight: '500 !important',
                }}
                className="heading5"
              >
                {` ${cardValue?.value || '-.-'}  ${t(
                  'widgets.pollutionCard.trees'
                )} `}
              </span>
              {t(props.description.line_3)}
            </span>
          </div>
        );
      case 'ranking[label+value]':
        return (
          <div>
            <span key={`${props.id}-label-value-label-value-array-ranking-label`} className="paragraph">
              <span className="heading5"> {t(props.description)} </span>
            </span>
            <span key={`${props.id}-label-value-label-value-array-ranking-value`} className="paragraph">
              {getSiteRanking()}
            </span>
          </div>
        );
        break;
    }
  };



*/
// ... existing code ...
